import React from 'react';
import { Box, ButtonBase, GlobalStyles, Typography } from '@mui/material';
import { VisibilityOutlined } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useShellTranslation } from './useShellTranslation';
import { shellTokens } from './shellTokens';
import { useShell } from './ShellContext';
import { useImpersonation } from '../../contexts/ImpersonationContext';
import { impersonationSubjectName } from './notificationsView';

const { color, size, radius } = shellTokens;

/**
 * Below this the banner drops its Switch action and tightens its padding. The
 * pill, both actions and the eye do not fit a 320px viewport once French copy
 * is in play, and the shell clips the overflow. Switch is the one that goes:
 * the warning pill and Exit both have to survive, and switching accounts is
 * still reachable from the header's View-as control and ⌘⇧U.
 */
const BANNER_ROOMY_QUERY = '@media (min-width:480px)';

/**
 * The banner paints above modals (see its zIndex), and portaled overlays are
 * positioned from the viewport top, so a full-screen dialog or a full-height
 * drawer would put its own first row (Close, title) under the banner. While
 * impersonating, dialogs and modal drawers start below the banner instead, so
 * the banner sits over the backdrop and nothing of the overlay's own.
 * Popovers and menus are left alone: they position from their anchor, and an
 * offset root would shift them off it.
 *
 * Left/right papers stretch with top+bottom rather than `height: calc(100% -
 * banner)`. PaperProps that set `height`/`maxHeight` to `100vh`/`100dvh`
 * (Ask Mozi) would otherwise cap the paper at the full viewport and overflow
 * the bottom by the banner height.
 */
const BELOW_BANNER = `calc(100% - ${size.banner}px)`;
const overlayOffsetStyles = {
  'body .MuiDialog-root.MuiModal-root': { top: size.banner },
  'body .MuiDrawer-modal.MuiModal-root .MuiDrawer-paperAnchorLeft, body .MuiDrawer-modal.MuiModal-root .MuiDrawer-paperAnchorRight':
    { top: size.banner, bottom: 0, height: 'auto', maxHeight: 'none' },
  'body .MuiDrawer-modal.MuiModal-root .MuiDrawer-paperAnchorTop': { top: size.banner, maxHeight: BELOW_BANNER },
  'body .MuiDrawer-modal.MuiModal-root .MuiDrawer-paperAnchorBottom': { maxHeight: BELOW_BANNER },
};

export const ImpersonationBanner: React.FC = () => {
  const { t } = useShellTranslation();
  const navigate = useNavigate();
  const { setOverlay } = useShell();
  const { isImpersonating, impersonatedOrg, impersonatedBrand, impersonatedRole, stopImpersonation } =
    useImpersonation();

  if (!isImpersonating) return null;

  const who = impersonationSubjectName({ impersonatedOrg, impersonatedBrand, impersonatedRole }, t);
  const context = impersonatedOrg?.type
    ? t(`shell.viewAs.type.${impersonatedOrg.type === 'vendor' ? 'vendor' : 'store'}`)
    : impersonatedBrand
      ? t('shell.viewAs.type.brand')
      : t(`shell.role.${impersonatedRole ?? 'store'}`);

  const exit = () => {
    // stopImpersonation cancels and clears the account-scoped cache itself.
    stopImpersonation();
    navigate('/app/dashboard');
  };

  const action = (label: string, borderAlpha: string, textColor: string, invert: boolean, onClick: () => void) => (
    <ButtonBase
      onClick={onClick}
      sx={{
        height: 28,
        px: '12px',
        flexShrink: 0,
        border: `1px solid ${borderAlpha}`,
        borderRadius: radius.menuRow,
        fontSize: '13px',
        fontWeight: 700,
        color: textColor,
        whiteSpace: 'nowrap',
        '&:hover': invert
          ? { background: color.white, color: color.impersonation }
          : { background: 'rgba(255,255,255,.10)' },
      }}
    >
      {label}
    </ButtonBase>
  );

  return (
    <>
      <GlobalStyles styles={overlayOffsetStyles} />
      <Box
        // The full sentence is the tooltip: as visible copy it collapsed to an
        // unreadable stub at narrow widths.
        title={t('shell.impersonation.warning', { who })}
        sx={{
          height: size.banner,
          position: 'relative',
          flexShrink: 0,
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          px: '12px',
          [BANNER_ROOMY_QUERY]: { gap: '12px', px: '20px' },
          background: color.impersonation,
          // Above MUI modals/drawers (1300) so an open dialog never hides whose
          // account the admin is acting in; still below snackbars and tooltips.
          zIndex: (theme) => theme.zIndex.modal + 1,
        }}
      >
        <VisibilityOutlined sx={{ fontSize: 17, color: color.impersonationIcon, flexShrink: 0 }} />
        <Typography
          noWrap
          sx={{ fontSize: '13.5px', fontWeight: 700, color: color.white, minWidth: 0 }}
        >
          {t('shell.impersonation.viewing', { who, context })}
        </Typography>
        <Box
          component="span"
          sx={{
            flexShrink: 0,
            px: '8px',
            py: '2px',
            borderRadius: radius.pill,
            background: 'rgba(255,255,255,.16)',
            color: color.white,
            fontSize: '11.5px',
            fontWeight: 600,
          }}
        >
          {t('shell.impersonation.liveChanges')}
        </Box>
        <Box sx={{ flex: 1, minWidth: '8px' }} />
        <Box sx={{ display: 'none', [BANNER_ROOMY_QUERY]: { display: 'contents' } }}>
          {action(
            t('shell.impersonation.switch'),
            'rgba(255,255,255,.28)',
            color.impersonationAccent,
            false,
            () => setOverlay({ kind: 'palette', mode: 'as' }),
          )}
        </Box>
        {action(t('shell.impersonation.exit'), 'rgba(255,255,255,.35)', color.white, true, exit)}
      </Box>
    </>
  );
};
